import React, { useState, useRef, useEffect } from 'react'
import Icons from '~/Common-Objects/UI/Icons/index'
import i18n from '~/i18n'
import { useSelector, useDispatch } from 'react-redux'
import * as siteMasterActions from '~/data/actions/siteActions' //now we can use user actions here
import AiContentGenerator from '~/pages/cp/includes/ai/contentGenerator'
import lodashGet from 'lodash/get'
import { setAiContentAssistantDrawer } from '~/Common-Objects/TemplateEditor/DragDropStage/Shared/aiContentAssistantLock'

export default function HolderOptions({
    path,
    copyElement,
    deleteElement,
    saveBlock,
    canDelete,
    user,
    moveElementUp,
    moveElementDown,
    elementData = {},
    elementsData = {}
}) {
    const dispatch = useDispatch()
    const siteMaster = useSelector((state) => state.siteMaster)

    const originalBottom = useRef(null)
    const toolbarRef = useRef(null)
    const isFixedRef = useRef(false)
    const flippedRef = useRef(false)
    const scrollRafRef = useRef(null)

    let canMoveUp = true
    let canMoveDown = true

    let parentPath = path.substring(0, path.lastIndexOf('.'))
    let parentElement = lodashGet(elementsData, parentPath)
    if (parentElement?.elements) {
        let siblingElements = parentElement.elements
        let siblingIndex = siblingElements.findIndex((element) => element.tuuid === elementData.tuuid)
        if (siblingIndex === 0) {
            canMoveUp = false
        }
        if (siblingIndex === siblingElements.length - 1) {
            canMoveDown = false
        }
    }

    const hasAi =
        elementData?.name?.toLowerCase() == 'text' ||
        elementData?.name?.toLowerCase() == 'paragraph' ||
        elementData?.name?.toLowerCase() == 'heading'
    let aiEnabled = siteMaster?.siteId == 1

    useEffect(() => {
        const dndScrollyArea = document.getElementById('dd_contentArea')
        if (!dndScrollyArea) return

        originalBottom.current = null
        isFixedRef.current = false
        flippedRef.current = false

        const onScroll = () => {
            if (scrollRafRef.current) {
                cancelAnimationFrame(scrollRafRef.current)
            }
            scrollRafRef.current = requestAnimationFrame(() => {
                scrollRafRef.current = null
                scrollListener()
            })
        }

        dndScrollyArea.addEventListener('scroll', onScroll)
        scrollListener()
        return () => {
            dndScrollyArea.removeEventListener('scroll', onScroll)
            if (scrollRafRef.current) {
                cancelAnimationFrame(scrollRafRef.current)
            }
        }
    }, [path, elementData?.tuuid])

    const scrollListener = () => {
        const ourStage = document.getElementById('dd_contentArea')
        const ourSelf = toolbarRef.current

        if (!ourStage || !ourSelf) return

        if (flippedRef.current) {
            const holder = ourSelf.closest('.dndEditorv2-elementHolder')
            const blockTop = holder ? holder.getBoundingClientRect().top : 0
            const blockBottom = holder ? holder.getBoundingClientRect().bottom : 0
            const stageBottom = ourStage.getBoundingClientRect().bottom
            if (blockTop > 0 && blockBottom > stageBottom - 160) return
            flippedRef.current = false
        }

        if (originalBottom.current === null) {
            originalBottom.current = window.getComputedStyle(ourSelf).bottom
        }

        ourSelf.style.position = 'absolute'
        ourSelf.style.bottom = originalBottom.current
        const stageRect = ourStage.getBoundingClientRect()
        const selfRect = ourSelf.getBoundingClientRect()
        const overflowThreshold = 8
        const isOffBottom =
            selfRect.bottom > stageRect.bottom + (isFixedRef.current ? -overflowThreshold : overflowThreshold)

        if (isOffBottom) {
            const parentRect = ourSelf.parentElement.getBoundingClientRect()
            const parentCenterX = parentRect.left + parentRect.width / 2
            ourSelf.style.position = 'fixed'
            ourSelf.style.left = `${parentCenterX}px`
            ourSelf.style.transform = 'translateX(-50%)'
            ourSelf.style.bottom = `${window.innerHeight - stageRect.bottom + 10}px`

            const fixedSelfRect = ourSelf.getBoundingClientRect()
            const fixedParentRect = ourSelf.parentElement.getBoundingClientRect()

            const isOffTop = fixedSelfRect.top < fixedParentRect.top
            if (isOffTop) {
                const holder = ourSelf.closest('.dndEditorv2-elementHolder')
                const holders = ourStage.querySelectorAll('.dndEditorv2-elementHolder')
                const lastHolder = holders[holders.length - 1]
                const lastBottom = lastHolder ? lastHolder.getBoundingClientRect().bottom : 0
                const isLastItem =
                    !!holder &&
                    !!lastHolder &&
                    (holder === lastHolder ||
                        (holder.contains(lastHolder) && Math.abs(fixedParentRect.bottom - lastBottom) < 48))
                const scrolledToBottom = ourStage.scrollHeight - ourStage.scrollTop - ourStage.clientHeight <= 24
                if (scrolledToBottom && isLastItem && fixedParentRect.top > 0) {
                    ourSelf.style.position = 'absolute'
                    ourSelf.style.top = 'auto'
                    ourSelf.style.left = '50%'
                    ourSelf.style.transform = 'translateX(-50%)'
                    ourSelf.style.bottom = 'calc(100% + 38px)'
                    flippedRef.current = true
                    isFixedRef.current = false
                } else {
                    ourSelf.style.bottom = `${window.innerHeight - fixedParentRect.top - fixedSelfRect.height}px`
                    isFixedRef.current = true
                }
            } else {
                isFixedRef.current = true
            }
        } else {
            ourSelf.style.position = 'absolute'
            ourSelf.style.bottom = originalBottom.current
            ourSelf.style.left = '50%'
            ourSelf.style.transform = 'translateX(-50%)'
            isFixedRef.current = false
        }
    }

    const getEditor = (elementUuid) => {
        if (!elementUuid) return null
        const id = `redactor-${elementUuid}`
        const instances = window.Redactor?.instances || []
        return instances.find((editor) => editor.element && editor.element.id === id) || null
    }

    const closeAiDrawer = () => {
        setAiContentAssistantDrawer(null)
        dispatch(siteMasterActions.alterSiteDrawer(false, false, 'right', null, true))
    }

    const openAiContentAssistant = () => {
        const elementUuid = elementData?.tuuid || ''
        let selectedText = ''
        const editor = getEditor(elementUuid)
        if (editor) {
            const selection = editor.getService('selection')
            if (selection) selectedText = selection.getText() || ''
            editor.saveSelection()
        }

        var theDrawer = (
            <AiContentGenerator
                contentType='paragraph'
                selectedText={selectedText || undefined}
                insertText={async (generatedHtml) => {
                    const liveEditor = getEditor(elementUuid)
                    if (liveEditor) {
                        liveEditor.restoreSelection()
                        liveEditor.insertHtml(generatedHtml)
                    }
                    closeAiDrawer()
                }}
                close={() => {
                    const liveEditor = getEditor(elementUuid)
                    if (liveEditor) {
                        liveEditor.restoreSelection()
                    }
                    closeAiDrawer()
                }}
            />
        )

        // Keep this element selected while the assistant is open so its Redactor
        // instance is not destroyed by stage clicks (see sc-35334).
        setAiContentAssistantDrawer(theDrawer)

        dispatch(
            siteMasterActions.alterSiteDrawer(true, false, 'right', theDrawer, true, '700px', 'drawer_dark', () => {
                setAiContentAssistantDrawer(null)
            })
        )
    }

    return (
        <div
            ref={toolbarRef}
            className='dndEditorv2-ui-dd-tools dndEditorv2-ui-dd-tools-bottom'
            style={{ pointerEvents: 'auto' }}
            onMouseEnter={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
            onMouseLeave={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
            onMouseDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
            onMouseUp={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
            onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
        >
            {hasAi && aiEnabled && (
                <>
                    <div
                        className='dndEditorv2-ui-dd-tools-icon'
                        onMouseEnter={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                        }}
                        onMouseLeave={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                        }}
                        onMouseDown={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                        }}
                        onMouseUp={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                        }}
                        onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            openAiContentAssistant()
                        }}
                    >
                        <Icons
                            icon='aiWand'
                            color='#0A0A0A'
                            style={{ width: '18px', height: '18px' }}
                            alt={i18n.t('templates:aiContentAssistant.aiContentAssistant')}
                            title={i18n.t('templates:aiContentAssistant.aiContentAssistant')}
                        />
                    </div>

                    <div className='dndEditorv2-ui-dd-tools-divider'></div>
                </>
            )}

            <div
                className={'dndEditorv2-ui-dd-tools-icon' + (canMoveUp ? '' : ' dndEditorv2-ui-dd-tools-icon-disabled')}
                onClick={(event) => {
                    event.stopPropagation()
                    if (canMoveUp) {
                        moveElementUp(path)
                    }
                }}
            >
                <Icons
                    icon='arrowUpAlternative'
                    color='#0A0A0A'
                    style={{ width: '18px', height: '18px' }}
                    alt={i18n.t('copy')}
                    title={i18n.t('copy')}
                />
            </div>
            <div
                className={
                    'dndEditorv2-ui-dd-tools-icon' + (canMoveDown ? '' : ' dndEditorv2-ui-dd-tools-icon-disabled')
                }
                onClick={(event) => {
                    event.stopPropagation()
                    if (canMoveDown) {
                        moveElementDown(path)
                    }
                }}
            >
                <Icons
                    icon='arrowDownAlternative'
                    color='#0A0A0A'
                    style={{ width: '18px', height: '18px' }}
                    alt={i18n.t('copy')}
                    title={i18n.t('copy')}
                />
            </div>

            <div className='dndEditorv2-ui-dd-tools-divider'></div>
            {saveBlock && (
                <div
                    className='dndEditorv2-ui-dd-tools-icon'
                    onClick={(event) => {
                        event.stopPropagation()
                        saveBlock(true)
                    }}
                >
                    <Icons
                        icon='starNew'
                        color='#0A0A0A'
                        style={{ width: '18px', height: '18px' }}
                        alt={i18n.t('Save')}
                        title={i18n.t('Save')}
                    />
                </div>
            )}

            <div
                className='dndEditorv2-ui-dd-tools-icon'
                onClick={(event) => {
                    event.stopPropagation()
                    copyElement(path)
                }}
            >
                <Icons
                    icon='clone'
                    color='#0A0A0A'
                    style={{ width: '18px', height: '18px' }}
                    alt={i18n.t('copy')}
                    title={i18n.t('copy')}
                />
            </div>
            {canDelete && (
                <div
                    className='dndEditorv2-ui-dd-tools-icon'
                    onClick={(event) => {
                        event.stopPropagation()
                        deleteElement(path)
                    }}
                >
                    <Icons
                        icon='binOutline'
                        color='#DD4340'
                        style={{ width: '18px', height: '18px' }}
                        alt={i18n.t('delete')}
                        title={i18n.t('delete')}
                    />
                </div>
            )}
        </div>
    )
}
